import { UseQueryOptions } from "@tanstack/react-query";
import { IClinicSummary } from "../library/models/clinicDashboard";
import { ITherapistSummary } from "../library/models/therapistDashboard";
import { BaseService } from "./baseService";

/**
 * The dashboard numbers, for whoever is entitled to which.
 *
 * Tenant-scoped like the rest: which clinic comes from the token, never from an argument.
 * The two endpoints are role-separated on the server, so each answers 403 rather than a
 * thinner result for the wrong caller.
 */
export class DashboardService extends BaseService {
    /**
     * Everything the dashboard renders, in one request.
     *
     * Both income figures come back together rather than the caller picking one, so the
     * page's Billed/Earned toggle is a state change and provably not a refetch. The query
     * key is the date range, so switching periods caches per range and switching back is
     * instant.
     */
    getClinicSummary(from: string, to: string): UseQueryOptions<IClinicSummary, Error> {
        return this.createGetQuery<IClinicSummary>('/dashboard/clinic-summary', { from, to });
    }

    /**
     * The caller's own income, for the therapist page.
     *
     * There is no therapist argument and there must not be one: the server reads the
     * therapist off the token, so this cannot be pointed at a colleague by editing a
     * query string. Keyed on the range like its sibling, so switching years caches.
     */
    getTherapistSummary(from: string, to: string): UseQueryOptions<ITherapistSummary, Error> {
        return this.createGetQuery<ITherapistSummary>('/dashboard/therapist-summary', { from, to });
    }
}
